import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { compile } from '../../../utils/search'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import LoadingSquare from '../common/LoadingSquare'
import { fetchTaskLabelGroupCategoryLabelGroup } from '../../../states/containers/task/taskSlice'
import { RequestGetTaskLabelGroupCategoryLabelGroup } from '../../../states/types/task'

interface LabelGroup {
  id: number
  name: string
  code: string | null
  color: string
}

interface Props {
  labelGroupCategoryId: number
  labelGroupIdList: number[]
  callbackLabelGroups: (labelGroups: LabelGroup[]) => void
  labelGroupList: LabelGroup[]
  isClear?: boolean
  searchType?: string
}

const SEARCH_LIMIT = 1000

const AddLabelGroupModule: FC<Props> = (props) => {
  const {
    labelGroupCategoryId,
    labelGroupIdList,
    callbackLabelGroups,
    labelGroupList,
    isClear,
    searchType,
  } = props

  const {
    login,
  } = useSelector((state: RootState) => state)

  const dispatch = useDispatch()

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [selectLabelGroups, setSelectLabelGroups] = useState<LabelGroup[]>([])

  const [labelGroups, setLabelGroups] = useState<LabelGroup[]>([])
  const [searchLabelGroupWord, setSearchLabelGroupWord] = useState<string>("")

  const [isWordLoading, setIsWordLoading] = useState<boolean>(false)
  const [composition, setComposition] = useState<boolean>(false)
  const [isSearched, setIsSearched] = useState<boolean>(false)

  const labelGroupsInit = useMemo(() => (
    labelGroupList || []
  ), [labelGroupList])

  const resetLabelGroup = () => {
    setSearchLabelGroupWord("")
    setLabelGroups(labelGroupsInit)
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const getUniqueValues = ([...array]) => (
    array.filter((value, _index, self) => self.indexOf(value) === self.lastIndexOf(value))
  )

  const clickLabelGroup = (labelGroupId: number) => {
    if (selectLabelGroups.map((v) => v.id).includes(labelGroupId)) {
      setSelectLabelGroups(selectLabelGroups.filter((v) => v.id !== labelGroupId))
    } else {
      const tmp = labelGroups.find((v) => v.id === labelGroupId)
      if (tmp) setSelectLabelGroups([...selectLabelGroups, tmp])
    }
  }

  const searchLabelGroup = (word?: string) => {
    let words: string[] = []
    if (word !== undefined) {
      words = word.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    } else {
      words = searchLabelGroupWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    }
    if (!words.length) {
      resetLabelGroup()
      return
    }
    if (login.feature_config?.high_volume) {
      setIsWordLoading(true)
      const params: RequestGetTaskLabelGroupCategoryLabelGroup = {
        keywords: words,
        limit: SEARCH_LIMIT,
        search_type: searchType || "all",
      }
      if (labelGroupCategoryId) {
        params.label_group_category_id = labelGroupCategoryId.toString()
      }
      dispatch(
        fetchTaskLabelGroupCategoryLabelGroup(
          params,
          (result) => {
            setLabelGroups(result.label_groups)
            setIsWordLoading(false)
            setIsSearched(true)
          },
        ),
      )
      return
    }
    const list = labelGroupsInit.filter((labelGroup) => {
      if (labelGroup === null) return false
      return (compile(words).test(labelGroup.name.toLowerCase()))
    })
    setLabelGroups(sortBy(list, 'id'))
    setIsSearched(true)
  }

  const inputSearchWord = (word: string) => {
    setSearchLabelGroupWord(word)
    if (!login.feature_config?.high_volume) searchLabelGroup(word)
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const closeSearch = () => {
    setIsSearchDisplay(false)
    resetLabelGroup()
    if (!getUniqueValues([...selectLabelGroups.map((v) => v.id), ...labelGroupIdList]).length) return
    callbackLabelGroups(selectLabelGroups)
    setSelectLabelGroups([])
  }

  useEffect(() => {
    setLabelGroups(labelGroupList || [])
  }, [labelGroupList])

  useEffect(() => {
    if (!labelGroups) return
    if (!login.feature_config?.high_volume) {
      setSelectLabelGroups(labelGroups.filter((v) => labelGroupIdList.includes(v.id)))
    }
  }, [labelGroupIdList])

  useEffect(() => {
    if (!isClear) return
    setSelectLabelGroups([])
  }, [isClear])

  useEffect(() => {
    setIsSearched(false)
    const timer = setTimeout(() => {
      if (!composition) {
        searchLabelGroup()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchLabelGroupWord, composition])

  return (
    <>
      <div
        className="add_button"
        onClick={() => openSearch()}
        role="none"
      >
        <FontAwesomeIcon icon={['fas', 'plus']} />
        &nbsp;
        追加
        <div className={`search_box natural ${isSearchDisplay || 'none'}`}>
          <div className="search_detail">
            <div className="category">
              <div className="category_title">
                プロジェクト
                {
                  selectLabelGroups.length ? (
                    <div
                      className="select_reset"
                      role="none"
                      onClick={() => setSelectLabelGroups([])}
                    >
                      リセット
                    </div>
                  ) : (
                    <></>
                  )
                }
              </div>
              <div className="search_area">
                <FontAwesomeIcon
                  icon={['fas', 'search']}
                  className="icon"
                />
                <input
                  type="text"
                  placeholder="プロジェクト名・プロジェクトコードで絞り込み"
                  onChange={(event) => inputSearchWord(event.target.value)}
                  onKeyDown={(event) => keyDown(event)}
                  onCompositionStart={() => setComposition(true)}
                  onCompositionEnd={() => setComposition(false)}
                  value={searchLabelGroupWord}
                />
                {
                  searchLabelGroupWord && (
                    <button type="button" className="reset" onClick={resetLabelGroup}>
                      <FontAwesomeIcon icon={['far', 'times-circle']} />
                    </button>
                  )
                }
              </div>
              {
                isWordLoading ? (
                  <LoadingSquare>
                    <>
                      プロジェクトデータを取得しています…
                    </>
                  </LoadingSquare>
                ) : (
                  <>
                    {
                      !labelGroups.length ? (
                        <>
                          <div
                            style={{
                              padding: "16px",
                              backgroundColor: "#f5f7ff",
                              textAlign: "center",
                              fontSize: "16px",
                              color: "#999999",
                            }}
                          >
                            {
                              searchLabelGroupWord.length ? (
                                <>
                                  {
                                    isSearched ? (
                                      "キーワードに該当するプロジェクトがありませんでした"
                                    ) : (
                                      "検索中…"
                                    )
                                  }
                                </>
                              ) : (
                                "キーワードを入力して、プロジェクトを検索してください"
                              )
                            }
                          </div>
                        </>
                      ) : (
                        <>
                          {
                            login.feature_config?.high_volume && labelGroups.length === SEARCH_LIMIT && (
                              <div
                                style={{
                                  display: "flex",
                                  flexFlow: "column",
                                  alignItems: "center",
                                  gap: "4px",
                                  border: "solid 1px #FF0000",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff2f2",
                                  color: "#FF0000",
                                  padding: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <div>
                                  {`検索上位${SEARCH_LIMIT}件が表示されています`}
                                </div>
                                <div>
                                  お探しのプロジェクトが見つからない時は、キーワードを詳細に入力してください
                                </div>
                              </div>
                            )
                          }
                          <div className="chip_list">
                            {
                              labelGroups.map((labelGroup) => (
                                <button
                                  type="button"
                                  className={`chip ${selectLabelGroups.map((v) => v.id).includes(labelGroup.id) ? "checked" : ""}`}
                                  key={`labelGroup_${labelGroup.id}`}
                                  onClick={() => clickLabelGroup(labelGroup.id)}
                                >
                                  {labelGroup.name}
                                  {labelGroup.code && `(${labelGroup.code})`}
                                </button>
                              ))
                            }
                          </div>
                        </>
                      )
                    }
                  </>
                )
              }
            </div>
          </div>
        </div>
      </div>
      <div className={`close_screen ${isSearchDisplay || 'none'}`} role="presentation" onClick={() => closeSearch()} />
    </>
  )
}

AddLabelGroupModule.defaultProps = {
  isClear: false,
  searchType: "all",
}

export default AddLabelGroupModule
